import type { GeometryElement } from 'jsxgraph';
import type { ElementDisplayConfig, FeatureElementDef, FeatureType } from '../../../graphpad.model';
import type { CreatedElement, ElementContext } from '../../element.model';
import { standardToVertex } from '../../functions/quadratic/quadratic.model';
import { BaseFeatureCreator } from '../feature.model';
import { resolveFeatureStyle, resolveFeatureVisibility } from '../feature-style.model';
import { AXIS_OF_SYMMETRY_DEFAULT_PARAMS, type AxisOfSymmetryParams } from './axis-of-symmetry.model';

/**
 * Creates axis of symmetry lines for functions.
 *
 * Supported function types:
 * - Quadratic: `a(x-h)² + k` → axis at `x = h`
 * - Absolute: `a|x-h| + k` → axis at `x = h`
 * - Cosine: `a·cos(b(x-c)) + d` → axes at `x = c + nπ/b` (at each extremum)
 * - Sine: `a·sin(b(x-c)) + d` → axes at `x = c + π/(2b) + nπ/b` (at each extremum)
 *
 * The axis is reactive - it updates when parent function params change.
 */
export class AxisOfSymmetryCreator extends BaseFeatureCreator {
  readonly featureType: FeatureType = 'axisOfSymmetry';
  override readonly computedValueKeys = ['x'] as const;

  create(
    id: string,
    def: FeatureElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const { board, elementFactory, featureTheme } = context;
    const params = def.params as AxisOfSymmetryParams | undefined;
    if (!params?.of) {
      throw new Error('AxisOfSymmetry feature requires params.of to specify the parent function');
    }
    const parentElement = context.elements.get(params.of);
    if (!parentElement) {
      throw new Error(`Parent element ${params.of} not found`);
    }

    const objects: GeometryElement[] = [];

    const lineStyle = resolveFeatureStyle('line', this.featureType, context, display);
    const visible = resolveFeatureVisibility(this.featureType, featureTheme, display);

    const count = params.count ?? AXIS_OF_SYMMETRY_DEFAULT_PARAMS.count ?? 1;

    // Track the primary axis getter for computed values
    let primaryGetAxisX: (() => number) | undefined;

    // Explicit x-value provided
    if (params.x !== undefined) {
      const explicitX = typeof params.x === 'number' ? () => params.x as number : params.x;
      primaryGetAxisX = typeof params.x === 'number' ? () => params.x as number : undefined;
      const axisResult = this.createAxisLine(elementFactory, explicitX, lineStyle, visible);
      objects.push(...axisResult);
      return this.buildResult(id, objects, board, primaryGetAxisX);
    }

    // Auto-detect based on function type
    switch (parentElement.type) {
      case 'quadratic': {
        // Standard form: axis at x = -b/(2a)
        const getAxisX = () => {
          const p = parentElement.getParams?.() ?? {};
          const { h } = standardToVertex(p['a'] ?? 1, p['b'] ?? 0, p['c'] ?? 0);
          return h;
        };
        primaryGetAxisX = getAxisX;
        const axisResult = this.createAxisLine(elementFactory, getAxisX, lineStyle, visible);
        objects.push(...axisResult);
        break;
      }
      case 'quadratic-vertex':
      case 'absolute': {
        // Vertex form: axis at x = h
        const getAxisXVertex = () => parentElement.getParams?.()?.['h'] ?? 0;
        primaryGetAxisX = getAxisXVertex;
        const axisResultVertex = this.createAxisLine(elementFactory, getAxisXVertex, lineStyle, visible);
        objects.push(...axisResultVertex);
        break;
      }

      case 'cosine': {
        // Axes at x = c + nπ/b (at each maximum/minimum)
        // First axis at x = c (first maximum since cos(0) = 1)
        for (let n = 0; n < count; n++) {
          const index = n;
          const getAxisX = () => {
            const p = parentElement.getParams?.() ?? {};
            const b = p['b'] ?? 1;
            const c = p['c'] ?? 0;
            return c + (index * Math.PI) / b;
          };
          if (n === 0) {
            primaryGetAxisX = getAxisX;
          }
          const axisResult = this.createAxisLine(elementFactory, getAxisX, lineStyle, visible);
          objects.push(...axisResult);
        }
        break;
      }

      case 'sine': {
        // Axes at x = c + π/(2b) + nπ/b (at each maximum/minimum)
        // First maximum at x = c + π/(2b)
        for (let n = 0; n < count; n++) {
          const index = n;
          const getAxisX = () => {
            const p = parentElement.getParams?.() ?? {};
            const b = p['b'] ?? 1;
            const c = p['c'] ?? 0;
            return c + Math.PI / (2 * b) + (index * Math.PI) / b;
          };
          if (n === 0) {
            primaryGetAxisX = getAxisX;
          }
          const axisResult = this.createAxisLine(elementFactory, getAxisX, lineStyle, visible);
          objects.push(...axisResult);
        }
        break;
      }

      default:
        console.warn(`AxisOfSymmetry: Function type '${parentElement.type}' does not have axis of symmetry support`);
    }

    return this.buildResult(id, objects, board, primaryGetAxisX);
  }

  private createAxisLine(
    elementFactory: ElementContext['elementFactory'],
    x: number | (() => number),
    lineStyle: ReturnType<typeof resolveFeatureStyle>,
    visible: boolean | undefined,
  ): GeometryElement[] {
    const objects: GeometryElement[] = [];

    const lineResult = elementFactory.line({
      a: [x, -1000],
      b: [x, 1000],
      name: 'axis',
      style: {
        ...lineStyle,
        visible: visible,
        straightFirst: true,
        straightLast: true,
      },
    });
    objects.push(lineResult.line);
    // Hide vertices - axis doesn't need visible endpoints
    lineResult.a.setAttribute({ visible: false });
    lineResult.b.setAttribute({ visible: false });

    return objects;
  }

  private buildResult(
    id: string,
    objects: GeometryElement[],
    board: ElementContext['board'],
    getAxisX?: () => number,
  ): CreatedElement {
    return {
      id,
      type: 'axisOfSymmetry',
      objects,
      getComputedValues: getAxisX
        ? () => ({
            x: getAxisX,
          })
        : undefined,
      destroy: () => objects.forEach((obj) => board.removeObject(obj)),
    };
  }
}
